import * as React from 'react'
import {
  Box,
  Button,
  Dialog,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import EditIcon from '@assets/Settings/EditIcons.svg'
import OpenEyeIcon from '@assets/OpenEye icon.svg'
import EyeIcon from '@assets/eye icon.svg'
import {
  formatPhone,
  type ManagedUser,
  type UserForm,
  type UserOptions,
} from '@/services/usersAdminService'

// The rules the Gateway checks (UserFields.java). The screen checks them first to explain them.
const USER_NAME = /^[a-z][a-z0-9]+(?:-[a-z][a-z0-9]+)*$/
const USER_NAME_MAX = 50
const EMAIL =
  /^[^\s@]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i
const PHONE = /^(0\d{8,9}|\+[1-9]\d{7,14})$/

type Field = 'displayName' | 'userName' | 'email' | 'phone' | 'sites' | 'groups'

// What each field accepts, shown under it, and the short error shown once the field is left with a bad value.
const GUIDE: Record<Field, { help: string; error: string }> = {
  displayName: { help: 'השם שמוצג ברשימה', error: 'יש להזין שם' },
  userName: {
    help: 'אנגלית קטנה, ספרות ומקף. למשל: dana-levi',
    error:
      'אותיות קטנות באנגלית, ספרות ומקף. כל חלק מתחיל באות ויש בו לפחות 2 תווים, עד 50 תווים',
  },
  email: {
    help: 'כתובת מלאה, למשל: name@company.co.il',
    error: 'כתובת המייל אינה תקינה',
  },
  phone: {
    help: 'לא חובה. למשל: 050-1234567',
    error: 'מספר לא תקין. למשל: 050-1234567',
  },
  sites: { help: 'האתרים שהמשתמש יראה', error: 'יש לבחור לפחות אתר אחד' },
  groups: {
    help: 'קובעת מה המשתמש יכול לעשות',
    error: 'יש לבחור לפחות קבוצה אחת',
  },
}

// The Gateway stores E.164; the form shows the local 050-1234567. Both reduce to the stored form.
const storedPhone = (phone: string) => {
  const digits = phone.replace(/[\s()-]/g, '')
  return digits.startsWith('0') ? `+972${digits.slice(1)}` : digits
}

const passwordRules = (password: string) => [
  { label: 'אותיות קטנות', valid: /[a-z]/.test(password) },
  { label: 'אותיות גדולות', valid: /[A-Z]/.test(password) },
  { label: 'מספרים', valid: /[0-9]/.test(password) },
  { label: 'תווים (!@#$%^&*)', valid: /[^A-Za-z0-9]/.test(password) },
  { label: 'מינימום 8 תווים', valid: password.length >= 8 },
]

type Props = {
  open: boolean
  // The user to edit, or null to create one.
  user: ManagedUser | null
  options: UserOptions
  isDarkMode: boolean
  onClose: () => void
  onSave: (form: UserForm) => Promise<void>
}

export default function UserFormDialog({
  open,
  user,
  options,
  isDarkMode,
  onClose,
  onSave,
}: Props) {
  const creating = user === null
  const [displayName, setDisplayName] = React.useState('')
  const [userName, setUserName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [phone, setPhone] = React.useState('')
  const [siteIds, setSiteIds] = React.useState<number[]>([])
  const [roleIds, setRoleIds] = React.useState<number[]>([])
  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [callerPassword, setCallerPassword] = React.useState('')
  const [showPassword, setShowPassword] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')
  const [touched, setTouched] = React.useState<Field[]>([])

  React.useEffect(() => {
    if (!open) return
    setDisplayName(user?.displayName ?? '')
    setUserName(user?.userName ?? '')
    setEmail(user?.email ?? '')
    setPhone(formatPhone(user?.phone ?? null))
    setSiteIds(user?.siteIds ?? [])
    setRoleIds(user?.roleIds ?? [])
    setPassword('')
    setConfirmPassword('')
    setCallerPassword('')
    setShowPassword(false)
    setSaving(false)
    setError('')
    setTouched([])
  }, [open, user])

  // A group bound to a site is offered only while that site is chosen.
  const groups = options.roles.filter(
    role => role.siteId === null || siteIds.includes(role.siteId)
  )
  const chosenGroups = roleIds.filter(id => groups.some(g => g.roleId === id))
  // A new e-mail or phone moves where the codes and alerts of a user go, so the Gateway wants the caller's own password.
  const contactChanged =
    !creating &&
    (email.trim().toLowerCase() !== (user?.email ?? '').toLowerCase() ||
      storedPhone(phone) !== (user?.phone ?? ''))
  const rules = passwordRules(password)
  const passwordWanted = creating || password !== '' || confirmPassword !== ''
  const passwordValid =
    !passwordWanted ||
    (rules.every(r => r.valid) && password === confirmPassword)
  const phoneValid =
    phone.trim() === '' || PHONE.test(phone.replace(/[\s()-]/g, ''))
  const broken: Record<Field, boolean> = {
    displayName: displayName.trim() === '',
    userName:
      creating &&
      (userName.trim().length > USER_NAME_MAX ||
        !USER_NAME.test(userName.trim())),
    email: !EMAIL.test(email.trim()),
    phone: !phoneValid,
    sites: siteIds.length === 0,
    groups: chosenGroups.length === 0,
  }
  const touch = (field: Field) =>
    setTouched(list => (list.includes(field) ? list : [...list, field]))
  // The error replaces the help once the field was left with a bad value.
  const shown = (field: Field) => touched.includes(field) && broken[field]
  const guide = (field: Field) =>
    shown(field) ? GUIDE[field].error : GUIDE[field].help
  // What still blocks the save, shown to the user so a disabled button is never silent.
  const missing = [
    broken.displayName && 'שם',
    broken.userName &&
      'שם משתמש לכניסה: אותיות באנגלית קטנות, ספרות ומקף, מתחיל באות',
    broken.email && 'מייל: כתובת תקינה',
    broken.phone && 'טלפון: למשל 050-1234567',
    broken.sites && 'אתרים: לבחור לפחות אתר אחד',
    broken.groups && 'קבוצת הרשאות: לבחור לפחות קבוצה אחת',
    !passwordValid && 'סיסמה: לפי הכללים, ואימות זהה',
    contactChanged &&
      callerPassword === '' &&
      'הסיסמה שלך: נדרשת לשינוי מייל או טלפון',
  ].filter((reason): reason is string => reason !== false)
  const canSave = !saving && missing.length === 0

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      await onSave({
        ...(creating ? { userName: userName.trim() } : {}),
        displayName: displayName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        siteIds,
        roleIds: chosenGroups,
        ...(passwordWanted ? { password } : {}),
        ...(contactChanged ? { callerPassword } : {}),
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'השמירה נכשלה')
      setSaving(false)
    }
  }

  const textColor = isDarkMode ? '#FFFFFF' : '#003F61'
  const subTextColor = isDarkMode ? '#94A3B8' : '#8F98A5'
  const borderColor = isDarkMode ? '#374151' : '#D9E2EC'

  const labelSx = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '13px',
    color: subTextColor,
    right: 14,
    left: 'auto',
    transformOrigin: 'top right',
    transform: 'translate(0, 8px) scale(1)',
    '&.MuiInputLabel-shrink': { transform: 'translate(0, -9px) scale(0.75)' },
    '&.Mui-focused': { color: '#72A4EB' },
  }
  const outline = {
    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
      borderColor,
      textAlign: 'right',
    },
    '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#72A4EB',
    },
    '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#72A4EB',
      borderWidth: '1.5px',
    },
  }
  const helperSx = {
    textAlign: 'right',
    color: subTextColor,
    fontFamily: 'Rubik, sans-serif',
    fontSize: '12px',
    mx: 0.5,
    '&.Mui-error': { color: '#EF4444' },
  }
  const fieldWidth = { width: { xs: '100%', sm: '210px' } }
  const fieldSx = {
    ...fieldWidth,
    '& .MuiOutlinedInput-root': {
      height: '38px',
      borderRadius: '8px',
      bgcolor: isDarkMode ? '#172130' : '#FFFFFF',
      fontFamily: 'Rubik, sans-serif',
      fontSize: '14px',
      color: textColor,
      ...outline,
    },
    '& .MuiInputLabel-root': labelSx,
    '& .MuiFormHelperText-root': helperSx,
  }
  const selectSx = {
    height: '38px',
    borderRadius: '8px',
    bgcolor: isDarkMode ? '#172130' : '#FFFFFF',
    fontFamily: 'Rubik, sans-serif',
    fontSize: '14px',
    color: textColor,
    ...outline,
    '& .MuiSelect-icon': { right: 'auto', left: '8px', color: subTextColor },
    '& .MuiSelect-select': {
      paddingRight: '14px !important',
      paddingLeft: '32px !important',
      textAlign: 'right',
    },
  }
  const menuProps = {
    PaperProps: {
      sx: {
        direction: 'rtl',
        bgcolor: isDarkMode ? '#202B40' : '#FFFFFF',
        color: textColor,
      },
    },
  }

  const siteName = (id: number) =>
    options.sites.find(s => s.siteId === id)?.siteName ?? ''
  const groupName = (id: number) =>
    options.roles.find(r => r.roleId === id)?.name ?? ''

  const passwordField = (
    label: string,
    value: string,
    set: (v: string) => void
  ) => (
    <TextField
      label={label}
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={e => set(e.target.value)}
      size="small"
      autoComplete="new-password"
      sx={fieldSx}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
              size="small"
              aria-label={showPassword ? 'הסתרת הסיסמה' : 'הצגת הסיסמה'}
            >
              <img
                src={showPassword ? OpenEyeIcon : EyeIcon}
                alt=""
                style={{
                  width: 20,
                  height: 20,
                  filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                }}
              />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="user-form-title"
      slotProps={{
        backdrop: {
          style: {
            backgroundColor: isDarkMode
              ? 'rgba(15, 23, 42, 0.3)'
              : 'rgba(50, 59, 119, 0.12)',
            backdropFilter: 'blur(2px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          direction: 'rtl',
          borderRadius: '14px',
          px: { xs: 2.5, sm: 4 },
          py: 3,
          m: { xs: 1.5, sm: 4 },
          width: '100%',
          maxWidth: '760px',
          bgcolor: isDarkMode ? '#202B40' : '#FFFFFF',
          boxShadow: '0px 10px 30px rgba(50, 59, 119, 0.08)',
          position: 'relative',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="סגירה"
        sx={{
          position: 'absolute',
          left: 16,
          top: 16,
          color: isDarkMode ? '#94A3B8' : '#003F61',
          opacity: 0.6,
          '&:hover': { opacity: 1 },
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, mt: 0.5 }}
      >
        <Box
          component="img"
          src={EditIcon}
          alt=""
          sx={{
            width: 24,
            height: 24,
            filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
          }}
        />
        <Typography
          id="user-form-title"
          sx={{
            fontFamily: 'Rubik, sans-serif',
            fontSize: '20px',
            fontWeight: 500,
            color: textColor,
          }}
        >
          {creating ? 'יצירת משתמש' : 'עריכת פרטי משתמש'}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          label="שם"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          onBlur={() => touch('displayName')}
          error={shown('displayName')}
          helperText={guide('displayName')}
          size="small"
          sx={fieldSx}
        />
        <TextField
          label="שם משתמש לכניסה"
          value={userName}
          onChange={e => setUserName(e.target.value)}
          onBlur={() => touch('userName')}
          size="small"
          disabled={!creating}
          error={shown('userName')}
          helperText={creating ? guide('userName') : undefined}
          inputProps={{ dir: 'ltr' }}
          sx={{
            ...fieldSx,
            '& .Mui-disabled': { WebkitTextFillColor: subTextColor },
          }}
        />
        <TextField
          label="מייל"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onBlur={() => touch('email')}
          error={shown('email')}
          helperText={guide('email')}
          size="small"
          inputProps={{ dir: 'ltr' }}
          sx={fieldSx}
        />
        <TextField
          label="טלפון"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          size="small"
          onBlur={() => touch('phone')}
          error={shown('phone')}
          helperText={guide('phone')}
          inputProps={{ dir: 'ltr' }}
          sx={fieldSx}
        />
        <FormControl size="small" sx={fieldWidth} error={shown('sites')}>
          <InputLabel id="user-sites-label" sx={labelSx}>
            אתרים
          </InputLabel>
          <Select
            labelId="user-sites-label"
            label="אתרים"
            multiple
            value={siteIds}
            onChange={e => setSiteIds(e.target.value as number[])}
            onClose={() => touch('sites')}
            renderValue={ids => (ids as number[]).map(siteName).join(', ')}
            sx={selectSx}
            MenuProps={menuProps}
          >
            {options.sites.map(site => (
              <MenuItem key={site.siteId} value={site.siteId}>
                {site.siteName}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText sx={helperSx}>{guide('sites')}</FormHelperText>
        </FormControl>
        <FormControl size="small" sx={fieldWidth} error={shown('groups')}>
          <InputLabel id="user-groups-label" sx={labelSx}>
            קבוצת הרשאות
          </InputLabel>
          <Select
            labelId="user-groups-label"
            label="קבוצת הרשאות"
            multiple
            value={chosenGroups}
            onChange={e => setRoleIds(e.target.value as number[])}
            onClose={() => touch('groups')}
            renderValue={ids => (ids as number[]).map(groupName).join(', ')}
            sx={selectSx}
            MenuProps={menuProps}
          >
            {groups.length === 0 ? (
              <MenuItem disabled value="">
                {siteIds.length === 0
                  ? 'בחרו אתר קודם'
                  : 'אין קבוצת הרשאות זמינה. לא הוגדרה קבוצה, או שלא הוקצתה לכם'}
              </MenuItem>
            ) : (
              groups.map(group => (
                <MenuItem key={group.roleId} value={group.roleId}>
                  {group.name}
                </MenuItem>
              ))
            )}
          </Select>
          <FormHelperText sx={helperSx}>{guide('groups')}</FormHelperText>
        </FormControl>
      </Box>

      {contactChanged && (
        <TextField
          label="הסיסמה שלך"
          type="password"
          value={callerPassword}
          onChange={e => setCallerPassword(e.target.value)}
          size="small"
          autoComplete="current-password"
          helperText="נדרשת לשינוי מייל או טלפון"
          sx={{ ...fieldSx, mt: 2 }}
        />
      )}

      {!user?.self && (
        <>
          <Box sx={{ mt: 2, mb: 1.5 }}>
            <Typography
              sx={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '15px',
                fontWeight: 600,
                color: textColor,
                mb: 0.5,
              }}
            >
              {creating ? 'סיסמה זמנית' : 'איפוס סיסמה'}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '13px',
                color: subTextColor,
                mb: 1.25,
              }}
            >
              {creating
                ? 'המשתמש יחליף את הסיסמה בכניסה הראשונה'
                : 'השאירו ריק כדי לא לשנות. סיסמה חדשה מנתקת את המשתמש, והוא יחליף אותה בכניסה הבאה'}
            </Typography>
            <Box sx={{ height: '1px', bgcolor: borderColor }} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              mb: 2,
              mt: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {passwordField('סיסמה', password, setPassword)}
              {passwordField(
                'אימות סיסמה',
                confirmPassword,
                setConfirmPassword
              )}
            </Box>
            {passwordWanted && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'Rubik, sans-serif',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: textColor,
                  }}
                >
                  הסיסמה חייבת להכיל:
                </Typography>
                {rules.map(rule => (
                  <Box
                    key={rule.label}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {rule.valid ? (
                      <CheckIcon sx={{ color: '#10B981', fontSize: 15 }} />
                    ) : (
                      <CloseIcon sx={{ color: '#EF4444', fontSize: 15 }} />
                    )}
                    <Typography
                      sx={{
                        fontFamily: 'Rubik, sans-serif',
                        fontSize: '12px',
                        color: rule.valid
                          ? isDarkMode
                            ? '#81C784'
                            : '#10B981'
                          : isDarkMode
                            ? '#E57373'
                            : '#EF4444',
                      }}
                    >
                      {rule.label}
                    </Typography>
                  </Box>
                ))}
                {password !== confirmPassword && confirmPassword !== '' && (
                  <Typography
                    sx={{
                      fontFamily: 'Rubik, sans-serif',
                      fontSize: '12px',
                      color: '#EF4444',
                    }}
                  >
                    הסיסמאות אינן זהות
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </>
      )}

      {error && (
        <Typography
          role="alert"
          sx={{
            fontFamily: 'Rubik, sans-serif',
            fontSize: '13px',
            color: '#FF5B5B',
            mb: 1.5,
          }}
        >
          {error}
        </Typography>
      )}

      {missing.length > 0 && (
        <Box
          role="status"
          sx={{
            fontFamily: 'Rubik, sans-serif',
            fontSize: '12.5px',
            color: subTextColor,
            mb: 1.5,
          }}
        >
          <Typography
            component="span"
            sx={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 600 }}
          >
            כדי לשמור יש להשלים:
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pr: 2.5, pl: 0 }}>
            {missing.map(reason => (
              <li key={reason}>{reason}</li>
            ))}
          </Box>
        </Box>
      )}

      <Box
        sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 1 }}
      >
        <Button
          onClick={save}
          variant="contained"
          disabled={!canSave}
          sx={{
            bgcolor: '#72A4EB',
            color: '#FFFFFF',
            textTransform: 'none',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            borderRadius: '8px',
            minWidth: '90px',
            px: 3,
            py: 0.9,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#5A90DE', boxShadow: 'none' },
            '&.Mui-disabled': {
              bgcolor: isDarkMode ? '#374151' : '#E4E7EB',
              color: isDarkMode ? '#94A3B8' : '#8F98A5',
            },
          }}
        >
          {saving ? 'שומר...' : 'שמירה'}
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor,
            color: textColor,
            textTransform: 'none',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            borderRadius: '8px',
            minWidth: '90px',
            px: 3,
            py: 0.9,
            bgcolor: isDarkMode ? '#1E2530' : '#FFFFFF',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#F8FAFC',
              borderColor: '#72A4EB',
            },
          }}
        >
          ביטול
        </Button>
      </Box>
    </Dialog>
  )
}
